import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Controller } from 'react-hook-form'
import { toast } from 'sonner'

import {
  EnrollmentDisplayVersionEnum,
  getDisplayVersion
} from '../../../../api/enrollments/get-display-version'
import { getEnrollmentTermsAndConditions } from '../../../../api/enrollments/get-enrollment-terms-and-conditions'
import useMultipleSchema from '../../../../lib/hooks/use-multiple-schema'
import useTranslate from '../../../../lib/hooks/use-translate'
import { HobbyAudienceEnum } from '../../../../lib/types/enums/hobby/hobby-audience.enum'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentChildInput } from '../../../../lib/types/models/enrollment/enrollment-child-input'
import Form, { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import HobbiesSelection from '../../hobby/hobbies-section-selection'
import ClientEnrollmentChildCharacteristics from './client-enrollment-child-characteristics'
import ClientEnrollmentChildFamilyLetter from './client-enrollment-child-family-letter'
import ClientEnrollmentChildFamilySection from './client-enrollment-child-family-section'
import ClientEnrollmentChildInformationSection from './client-enrollment-child-information-section'
import ClientEnrollmentOtherInformation from './client-enrollment-other-information'
import ClientEnrolmentGeneralPhilosophy from './client-enrolment-general-philosophy'
import SecureFamilyImagesUploader from './secure-family-images-uploader'
import {
  clientEnrollmentChildProfileSaveForLaterSchema as saveForLaterSchema,
  clientEnrollmentChildProfileSubmissionSchema as submissionSchema
} from './utils'

type Props = {
  defaultValues?: EnrollmentChildInput
  enrollment: Pick<Enrollment, 'childFirstName' | 'childLastName'>
  className?: string
  enrollmentId?: number
  isTermsAndConditionsAccepted: boolean
  handleSubmit: (data: EnrollmentChildInput, isLongVersion: boolean) => Promise<void>
}

const ClientEnrollmentChildProfileForm = ({
  defaultValues,
  enrollmentId,
  enrollment,
  isTermsAndConditionsAccepted,
  handleSubmit
}: Props) => {
  const { t } = useTranslate()
  const { data: displayVersion } = useQuery({
    queryKey: ['display-version', enrollmentId],
    queryFn: () => {
      if (!enrollmentId) return { version: EnrollmentDisplayVersionEnum.SHORT }
      return getDisplayVersion(enrollmentId)
    },
    enabled: !!enrollmentId
  })

  const resolvedIsLongVersion = displayVersion?.version === EnrollmentDisplayVersionEnum.LONG

  // only links the terms and conditions when they can be fetched, the checkbox works without the link
  const { data: termsAndConditions } = useQuery({
    queryKey: ['enrollment-terms-and-conditions', enrollmentId],
    queryFn: () => {
      if (!enrollmentId) throw new Error('Enrollment ID is required')
      return getEnrollmentTermsAndConditions(enrollmentId)
    },
    enabled: !!enrollmentId && !!displayVersion && !resolvedIsLongVersion,
    retry: false
  })

  const { form, loading, submitForm } = useMultipleSchema<
    ['save-for-later', 'submit'],
    EnrollmentChildInput
  >(
    [
      { name: 'save-for-later', schema: saveForLaterSchema(t) },
      {
        name: 'submit',
        schema: submissionSchema(t, resolvedIsLongVersion, isTermsAndConditionsAccepted)
      }
    ],
    async (values, name) =>
      handleSubmit({ ...values, childInfoSubmitted: name === 'submit' }, resolvedIsLongVersion),
    defaultValues
  )
  const validateAndSubmit = async (submitType: 'save-for-later' | 'submit') => {
    const images = form.watch('familyImages') ?? []
    const errors: string[] = []

    // Validate images only for long version submission
    if (resolvedIsLongVersion && submitType === 'submit') {
      if (!images.length || images.length < 3) {
        errors.push(t('questionnaire.toast.error.familyImagesMinimum'))
      } else if (images.some((img) => !img.caption)) {
        errors.push(t('questionnaire.toast.error.familyImages'))
      }
    }

    // If there are image validation errors, show them and stop
    if (errors.length > 0) {
      toast.error(errors.join('\n'))
      return
    }

    // If images are valid (or not required), proceed with schema validation
    await submitForm(submitType)
  }

  if (!displayVersion) return <Loader center />

  const medicalReleaseCheckbox = (
    <InnerBox className="rounded-lg p-4 flex flex-col gap-3 ">
      <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
        {t('client.child.profile.medical.release.title')}
      </Typography>
      <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
        {t('client.child.profile.medical.release.long.text', {
          childFirstName: enrollment.childFirstName,
          childLastName: enrollment.childLastName?.toUpperCase()
        })}
      </Typography>

      <FormField
        control={form.control}
        name={'acceptMedicalGeneralTerms'}
        render={({ field, formState: { errors } }) => (
          <FormItem className={'flex-1'}>
            <FormControl>
              <Checkbox
                {...field}
                checked={field.value === true}
                hasContainer
                hasError={!!errors.acceptMedicalGeneralTerms}
                onChange={(checked) =>
                  form.setValue('acceptMedicalGeneralTerms', checked ? true : null, {
                    shouldValidate: true
                  })
                }
                component={
                  <Typography
                    variant="secondary-body"
                    color={
                      errors.acceptMedicalGeneralTerms
                        ? tailwindColors.secondaryRed.DEFAULT
                        : tailwindColors.primary.DEFAULT
                    }
                  >
                    {t('client.child.profile.medical.release.checkbox.label')}
                  </Typography>
                }
              />
            </FormControl>
          </FormItem>
        )}
      />
    </InnerBox>
  )

  const termsAndConditionsLink = termsAndConditions ? (
    <a
      href={termsAndConditions.url}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
      onClick={(event) => event.stopPropagation()}
    >
      {t('client.child.profile.terms.link')}
    </a>
  ) : (
    t('client.child.profile.terms.link')
  )

  const termsAndConditionsCheckbox = isTermsAndConditionsAccepted ? (
    <Checkbox
      checked
      disabled
      hasContainer
      wrapperClassName="bg-green-light"
      component={
        <Typography variant="secondary-body" color={tailwindColors.green.dark.DEFAULT}>
          {t('client.child.profile.terms.accepted.label')} {termsAndConditionsLink}
        </Typography>
      }
    />
  ) : (
    <FormField
      control={form.control}
      name={'acceptTermsAndConditions'}
      render={({ field, formState: { errors } }) => (
        <FormItem className={'flex-1'}>
          <FormControl>
            <Checkbox
              {...field}
              checked={field.value === true}
              hasContainer
              hasError={!!errors.acceptTermsAndConditions}
              onChange={(checked) =>
                form.setValue('acceptTermsAndConditions', checked ? true : null, {
                  shouldValidate: true
                })
              }
              component={
                <Typography
                  variant="secondary-body"
                  color={
                    errors.acceptTermsAndConditions
                      ? tailwindColors.secondaryRed.DEFAULT
                      : tailwindColors.primary.DEFAULT
                  }
                >
                  {t('client.child.profile.terms.checkbox.label')} {termsAndConditionsLink}
                </Typography>
              }
            />
          </FormControl>
        </FormItem>
      )}
    />
  )

  return (
    <Form form={form}>
      <form>
        <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
          <div className="flex flex-col gap-3">
            <ClientEnrollmentChildInformationSection
              version={resolvedIsLongVersion ? 'long' : 'short'}
            />
            <ClientEnrollmentChildCharacteristics />
            <Controller
              control={form.control}
              name="hobbies"
              render={({ field }) => (
                <HobbiesSelection
                  value={field.value || []}
                  onChange={field.onChange}
                  audience={HobbyAudienceEnum.ENROLLMENT}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-3">
            <ClientEnrollmentChildFamilySection />
            <ClientEnrollmentOtherInformation />
          </div>
        </div>

        {resolvedIsLongVersion && (
          <div className="flex flex-col gap-3.5 mt-3.5">
            <ClientEnrollmentChildFamilyLetter />
            <Controller
              control={form.control}
              name="familyImages"
              render={({ field }) => (
                <SecureFamilyImagesUploader
                  mode="primary"
                  images={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            <ClientEnrolmentGeneralPhilosophy enrollment={enrollment} />
            {medicalReleaseCheckbox}
          </div>
        )}

        {!resolvedIsLongVersion && <div className="mt-3.5">{termsAndConditionsCheckbox}</div>}

        <div className="flex justify-end mt-4 gap-2.5 mobile:flex-col mobile:gap-2">
          <Button
            label="Enregistrer et continuer plus tard"
            variant="ghost"
            type="button"
            loading={loading['save-for-later']}
            onClick={() => validateAndSubmit('save-for-later')}
          />
          <Button
            label="Valider et envoyer à LEC"
            variant="primary"
            type="button"
            loading={loading.submit}
            onClick={() => validateAndSubmit('submit')}
          />
        </div>
      </form>
    </Form>
  )
}

export default ClientEnrollmentChildProfileForm
